// UI操作関連のハンドラー
import { saveCategoryStateLocal } from './storage-handlers.js';
import { restorePreviewHeightLocal, startPreviewResize } from './preview-handlers.js';
import { createAppSection } from '../components/app-importer.js';
import { createCategoryHTML, createSectionHTML } from '../components/ui-generator.js';
import { getImportedApps } from '../state/app-state.js';
import { getSettings } from '../state/app-state.js';
import { t } from '../utils/i18n.js';
import { renderNICList } from './nic-handlers.js';

/**
 * セクションの折りたたみ/展開
 */
export function toggleSection(sectionId: string): void {
    const content = document.getElementById(sectionId);
    if (!content) return;
    
    const header = content.previousElementSibling;
    const arrow = header?.querySelector('.section-arrow');
    
    if (content.classList.contains('hidden')) {
        content.classList.remove('hidden');
        arrow?.classList.add('rotate-180');
    } else {
        content.classList.add('hidden');
        arrow?.classList.remove('rotate-180');
    }

    // セクション開閉状態を保存（カテゴリと一緒に管理）
    saveCategoryStateLocal();
}

/**
 * 大項目（カテゴリ）の折りたたみ/展開
 */
export function toggleCategory(categoryId: string): void {
    const content = document.getElementById(categoryId);
    if (!content) return;
    
    const header = content.previousElementSibling;
    const arrow = header?.querySelector('.category-arrow');
    
    if (content.classList.contains('hidden')) {
        content.classList.remove('hidden');
        arrow?.classList.add('rotate-180');
    } else {
        content.classList.add('hidden');
        arrow?.classList.remove('rotate-180');
    }

    // カテゴリ開閉状態を保存
    saveCategoryStateLocal();
}

/**
 * 右ペイン（プレビュー）の折りたたみ/展開（スマホのみ）
 */
export function togglePreview(): void {
    const previewPane = document.getElementById('previewPane');
    const toggleIcon = document.getElementById('previewToggleIcon');
    if (!previewPane || !toggleIcon) return;
    
    if (previewPane.classList.contains('hidden')) {
        previewPane.classList.remove('hidden');
        previewPane.classList.add('flex');
        toggleIcon.classList.add('rotate-180');
        // 保存された高さを復元
        restorePreviewHeightLocal();
        // リサイズハンドルのイベントリスナーを再設定（念のため）
        const previewResizeHandle = document.getElementById('previewResizeHandle');
        if (previewResizeHandle) {
            previewResizeHandle.addEventListener('mousedown', startPreviewResize);
            previewResizeHandle.addEventListener('touchstart', startPreviewResize, { passive: false });
        }
    } else {
        previewPane.classList.add('hidden');
        previewPane.classList.remove('flex');
        toggleIcon.classList.remove('rotate-180');
    }
}

/**
 * 全てのセクションを展開/折りたたみ
 */
export function toggleAllSections(expand: boolean): void {
    const categories = document.querySelectorAll('.category-content');
    const categoryArrows = document.querySelectorAll('.category-arrow');
    const sections = document.querySelectorAll('.section-content');
    const sectionArrows = document.querySelectorAll('.section-arrow');
    
    categories.forEach((category, index) => {
        if (expand) {
            category.classList.remove('hidden');
            categoryArrows[index]?.classList.add('rotate-180');
        } else {
            category.classList.add('hidden');
            categoryArrows[index]?.classList.remove('rotate-180');
        }
    });
    
    sections.forEach((section, index) => {
        if (expand) {
            section.classList.remove('hidden');
            sectionArrows[index]?.classList.add('rotate-180');
        } else {
            section.classList.add('hidden');
            sectionArrows[index]?.classList.remove('rotate-180');
        }
    });

    // 一括展開・折りたたみ時もカテゴリ状態を保存
    saveCategoryStateLocal();
}

/**
 * UIを生成（アプリケーションセクションを含む）
 */
export function renderUIWithApps(): void {
    const container = document.getElementById('settingsContainer');
    const countDiv = document.getElementById('selectedCount');
    if (!container) return;
    
    // 既存のカテゴリとセクションを削除
    const existingCategories = container.querySelectorAll('.category-container');
    existingCategories.forEach(category => category.remove());
    const existingSections = container.querySelectorAll('.section-container');
    existingSections.forEach(section => section.remove());
    
    // インポート済みアプリケーションセクション（後で既存カテゴリに統合する）
    const appSection = createAppSection(getImportedApps());
    
    // 大項目（カテゴリ）のHTMLを生成
    let categoriesHTML = '';
    const currentSettings = getSettings();
    if (currentSettings && currentSettings.categories && currentSettings.categories.length > 0) {
        categoriesHTML = currentSettings.categories.map((category, categoryIndex) => 
            createCategoryHTML(category, categoryIndex, createSectionHTML)
        ).join('');
    }
    
    // カテゴリを挿入
    const wrapper = document.createElement('div');
    wrapper.innerHTML = categoriesHTML;
    if (countDiv && countDiv.nextSibling && countDiv.parentNode) {
        countDiv.parentNode.insertBefore(wrapper, countDiv.nextSibling);
    } else if (countDiv && countDiv.parentNode) {
        countDiv.parentNode.appendChild(wrapper);
    }

    // 既存の「アプリケーション」カテゴリ内に Tree インポートUIとインポート済みアプリのセクションを統合
    const appCategoryContainer = container.querySelector('.category-container[data-category-title="アプリケーション"]') as HTMLElement | null;
    if (appCategoryContainer) {
        const categoryContent = appCategoryContainer.querySelector('.category-content') as HTMLElement | null;
        if (categoryContent) {
            // TreeインポートUI（常に表示）
            const importHTML = `
                <div class="mb-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                    <h3 class="text-xs font-bold mb-2 text-blue-400">${t('appImportTitle')}</h3>
                    <div class="mb-2 text-xs text-gray-400 space-y-1">
                        <p class="mb-1">${t('guideAppImportDesc')}</p>
                        <p class="font-semibold text-gray-300">${t('appImportUsage')}</p>
                        <ol class="list-decimal list-inside ml-2 space-y-0.5">
                            <li>${t('guideAppImportStep1')}</li>
                            <li><code class="bg-gray-900 px-1 rounded">tree /F</code> ${t('guideAppImportStep2')}</li>
                            <li>${t('guideAppImportStep3')}</li>
                        </ol>
                        <p class="mt-1 text-gray-500">${t('appImportTip')}</p>
                    </div>
                    <textarea id="treeInput" placeholder="${t('treeInputPlaceholder')}" class="w-full bg-gray-900 border border-gray-600 rounded p-2 text-white text-xs font-mono h-24 focus:outline-none focus:border-blue-500 transition"></textarea>
                    <div class="mt-2 flex gap-2">
                        <button id="importTreeButton" class="text-xs bg-blue-600 hover:bg-blue-500 px-3 py-1 rounded text-white font-bold transition">${t('importButton')}</button>
                        <button id="clearAppsButton" class="text-xs bg-gray-700 hover:bg-gray-600 px-3 py-1 rounded text-white transition">${t('clearButton')}</button>
                    </div>
                    <div id="importStatus" class="mt-2 text-xs text-gray-400"></div>
                </div>
            `;
            // 先頭にインポートUIを追加
            categoryContent.insertAdjacentHTML('afterbegin', importHTML);

            // インポート済みアプリがあれば、そのセクションをこのカテゴリに追加
            if (appSection && appSection.sections[0]) {
                const existingSectionCount = categoryContent.querySelectorAll('.section-container').length;
                const appsSectionHTML = createSectionHTML(appSection.sections[0], categoryContent.id || 'app_category', -1, existingSectionCount);
                categoryContent.insertAdjacentHTML('beforeend', appsSectionHTML);
            }
        }
    }
    
    // NIC設定リストを初期化
    setTimeout(() => {
        const currentSettings = getSettings();
        if (currentSettings && currentSettings.categories) {
            currentSettings.categories.forEach(category => {
                category.sections.forEach(section => {
                    section.items.forEach(item => {
                        if (item.isDynamicList && item.listItemType === 'nic') {
                            // NIC設定のレンダリング
                            renderNICList(item.id);
                        }
                    });
                });
            });
        }
    }, 100);
}

/**
 * UIを生成
 */
export function renderUI(): void {
    renderUIWithApps();
}

/**
 * UIテキストを翻訳に基づいて更新
 */
export function updateUITexts(): void {
    const isTableEditorView = !!document.getElementById('tableEditorContainer') && !document.getElementById('settingsContainer');

    // ヘッダー
    const headerTitle = document.querySelector('header h1');
    const headerSubtitle = document.querySelector('header p');
    if (headerTitle) headerTitle.textContent = t('appTitle');
    if (headerSubtitle) headerSubtitle.textContent = isTableEditorView ? t('tableEditorSubtitle') : t('appSubtitle');

    // ページタイトル（タブ）
    if (isTableEditorView) {
        document.title = t('tableEditorPageTitle');
    } else {
        document.title = t('appTitle');
    }

    // テーブルエディタ：メインへ戻るリンク
    const backToMainLink = document.getElementById('backToMainLink') as HTMLAnchorElement | null;
    if (backToMainLink) {
        backToMainLink.textContent = t('tableEditorBackToMain');
        backToMainLink.title = t('tableEditorBackToMainTitle');
    }

    // テーブルエディタ（モーダル）の見出し
    const tableEditorModalTitle = document.getElementById('tableEditorModalTitle');
    if (tableEditorModalTitle) {
        tableEditorModalTitle.textContent = t('tableEditorModalTitle');
    }
    
    // 検索・操作
    const searchInput = document.getElementById('searchInput') as HTMLInputElement | null;
    const expandAllBtn = document.getElementById('expandAllButton');
    const collapseAllBtn = document.getElementById('collapseAllButton');
    const clearSelectionBtn = document.getElementById('clearSelectionButton');
    if (searchInput) searchInput.placeholder = t('searchPlaceholder');
    if (expandAllBtn) {
        expandAllBtn.textContent = t('expandAll');
        expandAllBtn.title = t('expandAllTitle');
    }
    if (collapseAllBtn) {
        collapseAllBtn.textContent = t('collapseAll');
        collapseAllBtn.title = t('collapseAllTitle');
    }
    if (clearSelectionBtn) {
        clearSelectionBtn.textContent = t('clearSelection');
        clearSelectionBtn.title = t('clearSelectionTitle');
    }

    // ヘッダーのテーブル関連ボタン・編集中インジケーター
    const currentPCLabel = document.querySelector('#currentPCIndicator span');
    const currentPCPrevButton = document.getElementById('currentPCPrevButton');
    const currentPCNextButton = document.getElementById('currentPCNextButton');
    if (currentPCLabel) {
        currentPCLabel.textContent = t('headerEditing');
    }
    if (currentPCPrevButton) {
        currentPCPrevButton.title = t('headerPrevPC');
    }
    if (currentPCNextButton) {
        currentPCNextButton.title = t('headerNextPC');
    }
    
    // プレビュー
    const codeOutput = document.getElementById('codeOutput');
    const previewTitle = codeOutput?.previousElementSibling?.querySelector('span');
    const copyBtn = document.getElementById('copyButton');
    const downloadBtn = document.getElementById('downloadButton');
    const downloadRevertBtn = document.getElementById('downloadRevertButton');
    if (previewTitle) previewTitle.textContent = t('previewTitle');
    if (copyBtn) copyBtn.textContent = t('copyButton');
    if (downloadBtn) downloadBtn.textContent = t('downloadButton');
    if (downloadRevertBtn) {
        downloadRevertBtn.textContent = t('downloadRevertButton');
        downloadRevertBtn.title = t('downloadRevertTitle');
    }
    
    // ガイドセクション
    updateGuideTexts();
    
    // プレビューのヒント
    const previewTip = document.getElementById('previewTip');
    const previewRevertTip = document.getElementById('previewRevertTip');
    if (previewTip) previewTip.textContent = t('previewTip');
    if (previewRevertTip) previewRevertTip.textContent = t('previewRevertTip');
    
    // アプリケーションインポートセクション（存在する場合）
    const appImportTitle = document.querySelector('#app_category h3');
    const treeInput = document.getElementById('treeInput') as HTMLTextAreaElement | null;
    const importBtn = document.getElementById('importTreeButton');
    const clearBtn = document.getElementById('clearAppsButton');
    if (appImportTitle) appImportTitle.textContent = t('appImportTitle');
    if (treeInput) treeInput.placeholder = t('treeInputPlaceholder');
    if (importBtn) importBtn.textContent = t('importButton');
    if (clearBtn) clearBtn.textContent = t('clearButton');
}

/**
 * ガイドセクションのテキストを更新
 */
function updateGuideTexts(): void {
    const guideTitle = document.getElementById('guideToggle')?.querySelector('span');
    const guideContent = document.getElementById('guideContent');
    if (!guideContent) return;
    
    if (guideTitle) guideTitle.textContent = t('guideTitle');
    
    // ガイドコンテンツを再生成
    guideContent.innerHTML = `
        <div class="mb-3 p-3 bg-blue-900/20 rounded border border-blue-700/30">
            <p class="font-semibold text-blue-300 mb-1">${t('guideEfficiency')}</p>
            <p class="text-gray-400 leading-relaxed">
                ${t('guideEfficiencyDesc')}
            </p>
        </div>
        <div class="space-y-1.5">
            <p class="font-semibold text-gray-200">${t('guideBasicUsage')}</p>
            <ol class="list-decimal list-inside ml-2 space-y-1 text-gray-400">
                <li>${t('guideStep1')}</li>
                <li>${t('guideStep2')}</li>
                <li>${t('guideStep3')}</li>
            </ol>
        </div>
        <div class="pt-2 border-t border-gray-700 space-y-1.5">
            <p class="font-semibold text-gray-200">${t('guideKittingFlow')}</p>
            <ol class="list-decimal list-inside ml-2 space-y-1 text-gray-400">
                <li>${t('guideKittingStep1')}</li>
                <li>${t('guideKittingStep2')}</li>
                <li>${t('guideKittingStep3')}</li>
            </ol>
        </div>
        <div class="pt-2 border-t border-gray-700 space-y-1.5">
            <p class="font-semibold text-gray-200">${t('guideAppImport')}</p>
            <p class="text-gray-400 text-xs ml-2">${t('guideAppImportDesc')}</p>
            <ol class="list-decimal list-inside ml-4 space-y-1 text-gray-400 text-xs">
                <li>${t('guideAppImportStep1')}</li>
                <li><code class="bg-gray-800 px-1 rounded">tree /F</code> ${t('guideAppImportStep2')}</li>
                <li>${t('guideAppImportStep3')}</li>
            </ol>
        </div>
        <div class="pt-2 border-t border-gray-700 space-y-1.5">
            <p class="font-semibold text-gray-200">${t('guideRevert')}</p>
            <p class="text-gray-400 text-xs ml-2">${t('guideRevertDesc')}</p>
            <ul class="list-disc list-inside ml-4 space-y-1 text-gray-400 text-xs">
                <li>${t('guideRevertStep1')}</li>
                <li>${t('guideRevertStep2')}</li>
                <li>${t('guideRevertStep3')}</li>
            </ul>
        </div>
        <div class="pt-2 border-t border-gray-700">
            <p class="font-semibold text-gray-200 mb-1">${t('guideWarning')}</p>
            <ul class="list-disc list-inside ml-2 space-y-1 text-gray-400">
                <li>${t('guideWarning1')}</li>
                <li>${t('guideWarning2')}</li>
                <li>${t('guideWarning3')}</li>
            </ul>
        </div>
    `;
}

